/**
 * 通用状态（PRD §3.1.3、TECH-BRIEF §5）：加载中、空、错误、离线 / 数据过期、维护。
 * 文案全部由调用方传入。
 */
import type { CSSProperties, ReactNode } from 'react';
import { cx } from '../cx.js';
import { Button } from './Button.js';

export interface SkeletonProps {
  className?: string;
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  /** 圆形（头像、圆点） */
  circle?: boolean;
}

/** 骨架屏块。读屏忽略；加载区域请在容器上加 aria-busy。 */
export function Skeleton({ className, width, height = '1em', circle = false }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      data-skeleton=""
      className={cx('block animate-pulse bg-surface-2', circle ? 'rounded-full' : 'rounded-md', className)}
      style={{ width, height }}
    />
  );
}

export interface EmptyStateProps {
  /** 一句"现在怎样"，例如"你还没有机器" */
  title: ReactNode;
  description?: ReactNode;
  /** 一个主操作，例如 <Button>租第一台机器</Button> */
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div className={cx('flex flex-col items-center gap-3 px-4 py-10 text-center', className)}>
      {icon && <div aria-hidden="true">{icon}</div>}
      <p className="text-lg font-semibold text-fg">{title}</p>
      {description && <p className="text-fg-muted">{description}</p>}
      {action && <div className="mt-2 w-full max-w-xs">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  /** 发生了什么，例如"未卖出" */
  title: ReactNode;
  /** 钱是否受影响，例如"CU 仍在你的余额里" */
  fundsNote?: ReactNode;
  /** 补充说明 */
  description?: ReactNode;
  /** 下一步（主操作） */
  action?: ReactNode;
  /** 「联系客服」入口（PRD §3.1.3 错误态附「联系客服」） */
  support?: ReactNode;
  className?: string;
}

/** 错误态：不显示错误码（PRD §3.1.3）。 */
export function ErrorState({ title, fundsNote, description, action, support, className }: ErrorStateProps) {
  return (
    <div role="alert" className={cx('flex flex-col items-center gap-3 px-4 py-10 text-center', className)}>
      <p className="text-lg font-semibold text-fg">{title}</p>
      {fundsNote && <p className="font-medium text-fg">{fundsNote}</p>}
      {description && <p className="text-fg-muted">{description}</p>}
      {(action || support) && (
        <div className="mt-2 flex w-full max-w-xs flex-col gap-2">
          {action}
          {support}
        </div>
      )}
    </div>
  );
}

export interface OfflineBannerProps {
  /** 例如"网络已断开 · 数据更新于 14:02" */
  message: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  className?: string;
}

/** 离线 / 数据过期横幅（PRD §3.1.3、§8.3「弱网」）：保留最近数据并标"数据更新于 14:02"，提供「重试」。 */
export function OfflineBanner({ message, retryLabel, onRetry, className }: OfflineBannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cx(
        'flex items-center justify-between gap-3 border-b border-border bg-surface-2 px-4 py-1 text-sm text-fg',
        className,
      )}
    >
      <span className="flex items-center gap-2">
        {/* 中性圆点：离线不是"待领取"，不用状态色（D-P0-09-24） */}
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-fg-muted" />
        {message}
      </span>
      {onRetry && retryLabel && (
        <Button variant="text" onClick={onRetry} className="shrink-0 text-sm">
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export interface MaintenanceNoticeProps {
  /** 例如"维护中 · 预计 14:40 恢复" */
  message: ReactNode;
  /** 第二行说明（应急开关的用户端说明，platform C.5 <AvailabilityNotice>） */
  note?: ReactNode;
  className?: string;
}

/** 维护提示（PRD §3.1.3：主按钮置灰并显示预计恢复时间，由页面控制按钮）。 */
export function MaintenanceNotice({ message, note, className }: MaintenanceNoticeProps) {
  return (
    <div role="status" className={cx('rounded-xl border border-border bg-surface-2 px-4 py-3 text-sm text-fg', className)}>
      <p>{message}</p>
      {note && <p className="mt-1 text-fg-muted">{note}</p>}
    </div>
  );
}
